import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, X, Package, ImagePlus, Trash2, Sparkles, Loader2 } from "lucide-react";
import { MOWER_TYPES, BRANDS, newUnit } from "@/data/options";
import { AgentSelector } from "@/components/AgentSelector";
import { CustomerSection } from "@/components/CustomerSection";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

const inputCls =
  "focus-visible:ring-2 focus-visible:ring-[#F97316]/30 focus-visible:border-[#F97316]";

export default function UnitDetails({
  units,
  setUnits,
  photos,
  setPhotos,
  salesAgents,
  setSalesAgents,
  customers,
  setCustomers,
  maxCustomers,
  mowerCost,
  setMowerCost,
  shippingPrice,
}) {
  const [dimLoading, setDimLoading] = useState(null);

  const updUnit = (id, k, v) => setUnits(units.map((u) => (u.id === id ? { ...u, [k]: v } : u)));
  const addUnit = () => setUnits([...units, newUnit()]);
  const removeUnit = (id) => setUnits(units.length > 1 ? units.filter((u) => u.id !== id) : units);

  const suggestDims = async (u) => {
    setDimLoading(u.id);
    try {
      const { data } = await api.post("/ai/suggest-dimensions", {
        type: u.type,
        brand: u.brand,
        model: u.model,
      });
      setUnits((prev) =>
        prev.map((x) =>
          x.id === u.id
            ? { ...x, length: data.length ?? x.length, width: data.width ?? x.width, height: data.height ?? x.height, weight: data.weight ?? x.weight }
            : x
        )
      );
      toast.success(data.note ? `Estimated (${data.note})` : "Dimensions estimated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not estimate dimensions");
    } finally {
      setDimLoading(null);
    }
  };

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await api.post("/photos", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const url = `${API}/photos/${data.id}`;
        setPhotos((prev) => [...prev, { id: data.id, src: url, url, name: file.name }]);
      } catch {
        toast.error(`Could not upload ${file.name}`);
      }
    }
  };
  const removePhoto = (id) => setPhotos((prev) => prev.filter((p) => p.id !== id));

  return (
    <div className="bg-white border border-border rounded-lg shadow-sm p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Package className="h-4 w-4 text-[#F97316]" />
        <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-[#1C1917]">Unit Details</h3>
      </div>

      {units.map((u, idx) => (
        <div key={u.id} className="rounded-lg border border-border p-4 relative" data-testid={`unit-block-${idx}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wide text-[#F97316]">Unit {idx + 1}</span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 border-[#F97316]/40 text-[#EA580C] hover:bg-[#FFF7ED] text-xs"
                onClick={() => suggestDims(u)}
                disabled={dimLoading === u.id}
                data-testid={`suggest-dims-${idx}`}
              >
                {dimLoading === u.id ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3 mr-1" />
                )}
                Suggest dims
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-[#57534E] hover:text-red-600"
                onClick={() => removeUnit(u.id)}
                data-testid={`remove-unit-${idx}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2 md:col-span-1">
              <Label className="text-[10px] font-bold uppercase text-[#57534E]">Type</Label>
              <Select value={u.type} onValueChange={(v) => updUnit(u.id, "type", v)}>
                <SelectTrigger className={`mt-1 ${inputCls}`} data-testid={`unit-type-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MOWER_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 md:col-span-1">
              <Label className="text-[10px] font-bold uppercase text-[#57534E]">Brand</Label>
              <Select value={u.brand} onValueChange={(v) => updUnit(u.id, "brand", v)}>
                <SelectTrigger className={`mt-1 ${inputCls}`} data-testid={`unit-brand-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BRANDS.map((b) => (
                    <SelectItem key={b} value={b}>
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase text-[#57534E]">Model</Label>
              <Input className={`mt-1 ${inputCls}`} value={u.model} onChange={(e) => updUnit(u.id, "model", e.target.value)} data-testid={`unit-model-${idx}`} />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase text-[#57534E]">Qty</Label>
              <Input type="number" min="1" className={`mt-1 ${inputCls}`} value={u.qty} onChange={(e) => updUnit(u.id, "qty", e.target.value)} data-testid={`unit-qty-${idx}`} />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase text-[#57534E]">Length (in)</Label>
              <Input type="number" className={`mt-1 ${inputCls}`} value={u.length} onChange={(e) => updUnit(u.id, "length", e.target.value)} data-testid={`unit-length-${idx}`} />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase text-[#57534E]">Width (in)</Label>
              <Input type="number" className={`mt-1 ${inputCls}`} value={u.width} onChange={(e) => updUnit(u.id, "width", e.target.value)} data-testid={`unit-width-${idx}`} />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase text-[#57534E]">Height (in)</Label>
              <Input type="number" className={`mt-1 ${inputCls}`} value={u.height} onChange={(e) => updUnit(u.id, "height", e.target.value)} data-testid={`unit-height-${idx}`} />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase text-[#57534E]">Weight (lbs)</Label>
              <Input type="number" className={`mt-1 ${inputCls}`} value={u.weight} onChange={(e) => updUnit(u.id, "weight", e.target.value)} data-testid={`unit-weight-${idx}`} />
            </div>
            {customers.filter((c) => (c.name || "").trim()).length > 1 && (
              <div className="col-span-2">
                <Label className="text-[10px] font-bold uppercase text-[#57534E]">For customer (share quotes)</Label>
                <Select
                  value={(u.customer || "").trim() || "__any__"}
                  onValueChange={(v) => updUnit(u.id, "customer", v === "__any__" ? "" : v)}
                >
                  <SelectTrigger className={`mt-1 ${inputCls}`} data-testid={`unit-customer-${idx}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">All customers (shared)</SelectItem>
                    {customers
                      .filter((c) => (c.name || "").trim())
                      .map((c) => (
                        <SelectItem key={c.id || c.name} value={c.name.trim()}>
                          {c.name.trim()}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      ))}

      <Button variant="outline" size="sm" onClick={addUnit} className="border-dashed border-[#1C1917]/30" data-testid="add-unit-button">
        <Plus className="h-4 w-4 mr-1" /> Add unit
      </Button>

      {/* Photos */}
      <div className="pt-2">
        <Label className="text-xs font-bold uppercase tracking-[0.1em] text-[#57534E]">Shipment Photos</Label>
        <div className="mt-2 flex flex-wrap gap-3">
          {photos.map((p) => (
            <div key={p.id} className="relative group" data-testid={`photo-thumb-${p.id}`}>
              <img src={p.src} alt={p.name} className="h-24 w-32 object-cover rounded-md border border-border" />
              <button
                onClick={() => removePhoto(p.id)}
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-600 text-white flex items-center justify-center shadow"
                data-testid={`remove-photo-${p.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          <label className="h-24 w-32 rounded-md border-2 border-dashed border-[#1C1917]/30 flex flex-col items-center justify-center cursor-pointer hover:border-[#F97316] transition-colors text-[#57534E]">
            <ImagePlus className="h-6 w-6" />
            <span className="text-xs mt-1">Add photos</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} data-testid="photo-upload-input" />
          </label>
        </div>
      </div>

      <CustomerSection
        customers={customers}
        setCustomers={setCustomers}
        maxCustomers={maxCustomers}
        mowerCost={mowerCost}
        setMowerCost={setMowerCost}
        shippingPrice={shippingPrice}
      />

      <AgentSelector salesAgents={salesAgents} setSalesAgents={setSalesAgents} />
    </div>
  );
}
